import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg } from "@/lib/api";
import { fullName } from "@/lib/crm";

const PURPOSES = ["Discovery Call", "Phone Call", "Online Conference via Google Meet"];

const leadDescription = (c) =>
  c ? `Lead: ${fullName(c)}\nEmail: ${c.email || "—"}\nPhone: ${c.phone || "—"}\n\n` : "";

export default function AppointmentDialog({ open, onOpenChange, contacts, initial, onSaved }) {
  const [f, setF] = useState({});
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));

  useEffect(() => {
    if (open) setF({ contact_id: "", title: "AVY Discovery Call", date: "", time: "10:00", purpose: "Discovery Call", assignee: "", drive_link: "", description: "", ...initial });
  }, [open, initial]);

  const pickContact = (id) => {
    const c = contacts.find((x) => x.id === id);
    setF((s) => ({ ...s, contact_id: id, description: s.description && !s.description.startsWith("Lead:") ? s.description : leadDescription(c) }));
  };

  const save = async (e) => {
    e.preventDefault();
    if (!f.contact_id) return toast.error("Select a contact");
    try {
      await api.post("/appointments", f);
      toast.success("Appointment saved to calendar");
      onOpenChange(false);
      onSaved();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="appointment-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">New Appointment</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-4">
          <div className="space-y-1.5">
            <Label>Lead *</Label>
            <Select value={f.contact_id} onValueChange={pickContact}>
              <SelectTrigger data-testid="appt-contact-select"><SelectValue placeholder="Select lead" /></SelectTrigger>
              <SelectContent>{contacts.map((c) => <SelectItem key={c.id} value={c.id} data-testid={`appt-contact-option-${c.id}`}>{fullName(c)}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Title *</Label>
            <Input required value={f.title || ""} onChange={(e) => set("title")(e.target.value)} data-testid="appt-title-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Date *</Label><Input type="date" required value={f.date || ""} onChange={(e) => set("date")(e.target.value)} data-testid="appt-date-input" /></div>
            <div className="space-y-1.5"><Label>Time *</Label><Input type="time" required value={f.time || ""} onChange={(e) => set("time")(e.target.value)} data-testid="appt-time-input" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Purpose</Label>
              <Select value={f.purpose} onValueChange={set("purpose")}>
                <SelectTrigger data-testid="appt-purpose-select"><SelectValue placeholder="Select purpose" /></SelectTrigger>
                <SelectContent>{PURPOSES.map((p) => <SelectItem key={p} value={p} data-testid={`appt-purpose-option-${p.replace(/\s+/g, "-")}`}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Assignee</Label><Input value={f.assignee || ""} onChange={(e) => set("assignee")(e.target.value)} placeholder="VA or team member" data-testid="appt-assignee-input" /></div>
          </div>
          <div className="space-y-1.5"><Label>Google Drive attachment (optional)</Label><Input value={f.drive_link || ""} onChange={(e) => set("drive_link")(e.target.value)} placeholder="https://drive.google.com/…" data-testid="appt-drive-input" /></div>
          <div className="space-y-1.5">
            <Label>Description (lead info)</Label>
            <Textarea rows={4} value={f.description || ""} onChange={(e) => set("description")(e.target.value)} data-testid="appt-description-input" />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" className="bg-[#3B185F]" data-testid="appt-save-btn">Save Appointment</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
